<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { computed, onBeforeMount, ref } from 'vue';

  import type { Call, CommunicationChannel, Partner } from '@stuut/api/models';
  import { formatPhoneNumber } from '@stuut/common/src/utils';

  import { stuut } from '@/api';
  import { useTaskCollectionStore } from '@/store';

  const emit = defineEmits<{
    (e: 'openContactEditor'): void;
  }>();
  const taskCollectionStore = useTaskCollectionStore();
  const { currentTask } = storeToRefs(taskCollectionStore);
  const taskCall = ref<Call>();
  const isShowEmail = ref(false);

  const contactTypeMap = {
    call: 'phone number',
    email: 'email address',
  };

  const partner = ref<Partner | null>(null);
  const primaryEmailContactId = ref<string>();
  const primaryPhoneContactId = ref<string>();

  const source = computed<CommunicationChannel>(
    () =>
      (currentTask?.value?.acknowledgeTaskData?.data?.source as CommunicationChannel) || 'email',
  );
  const partnerId = ref<string | null>(currentTask?.value?.partner_id ?? null);

  const isRecordingConsentGiven = ref(
    currentTask?.value?.callData?.recording_consent_granted ?? true,
  );
  const isPrimaryDisabled = ref(currentTask?.value?.acknowledgeTaskData?.data?.is_primary_disabled);

  const newPrimaryContactObj = ref(
    currentTask?.value?.acknowledgeTaskData?.data?.new_primary_contact,
  );

  const didDisable = computed(() =>
    currentTask?.value?.acknowledgeTaskData?.data?.reason?.startsWith('LLM disabled:'),
  );

  const didNotDisable = computed(() =>
    currentTask?.value?.acknowledgeTaskData?.data?.reason?.startsWith('LLM did not disable:'),
  );

  const isResultOfUserResponse = computed(() => didDisable.value || didNotDisable.value);

  const contactValue = computed(() => {
    const value = currentTask?.value?.acknowledgeTaskData?.data?.contact_value || '';

    if (source.value === 'call') return formatPhoneNumber(value);

    return value;
  });

  const newPrimaryContact = computed(() => {
    if (newPrimaryContactObj.value?.id?.startsWith('ptn_')) {
      return partner.value?.allContacts?.find((contact) => contact?.id === '-1');
    }

    return partner.value?.allContacts?.find(
      (contact) => contact?.id === newPrimaryContactObj.value?.id,
    );
  });

  onBeforeMount(async () => {
    isShowEmail.value = Boolean(didNotDisable.value);

    if (partnerId.value) {
      partner.value = await stuut.partners.retrieve(partnerId.value);

      primaryEmailContactId.value = partner.value.primaryEmailContact?.id;
      primaryPhoneContactId.value = partner.value.primaryPhoneContact?.id;
    }

    if (currentTask?.value?.acknowledgeTaskData?.data?.call_id) {
      taskCall.value = await stuut.calls.retrieve(
        currentTask?.value?.acknowledgeTaskData?.data?.call_id,
      );
    }
  });
</script>

<template>
  <p class="font-bold text-muted-foreground">Task brief</p>

  <p>
    <strong>{{ currentTask?.label }}</strong>
  </p>

  <template v-if="isResultOfUserResponse">
    <p>
      Our attempt to reach
      <strong>{{ contactValue }}</strong> ({{ partner?.name }}) was responded with a

      <template v-if="didNotDisable"> potential</template>

      request to disable this contact.
    </p>
  </template>

  <template v-else-if="newPrimaryContact">
    <p>
      Our attempt to reach
      <strong>{{ contactValue }}</strong> ({{ partner?.name }}) failed. We've disabled that
      {{ contactTypeMap[source] }}
      and set a new primary contact for future outreach.
    </p>
    <p v-if="!isRecordingConsentGiven">
      They did <strong>not grant consent for recording</strong>. Please follow up manually.
    </p>
  </template>

  <template v-else>
    <p>
      Our attempt to reach
      <strong>{{ contactValue }}</strong> ({{ partner?.name }}) failed. We've disabled that
      {{ contactTypeMap[source] }}.
    </p>
    <p v-if="!isRecordingConsentGiven">
      They did <strong>not grant consent for recording</strong>. Please follow up manually.
    </p>
  </template>

  <p v-if="!newPrimaryContact && isPrimaryDisabled">
    <strong>
      <span class="capitalize">{{ source === 'call' ? 'phone' : source }} </span>
      outreach is disabled
    </strong>
    until you choose or

    <span class="text-blue hover:underline cursor-pointer" @click="emit('openContactEditor')">
      create
    </span>

    a new primary {{ source === 'call' ? 'phone' : source }} contact.
  </p>
</template>

<style scoped></style>
